import React, { useState } from 'react';
import { Card, Button, Form, Alert, Spinner, Image } from 'react-bootstrap';
import { useUploadFile } from "../../../../api/fileUpload/fileUpload.hooks";

const FileUploadSection = () => {
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [uploadedFileType, setUploadedFileType] = useState(null);
  const [uploadAlert, setUploadAlert] = useState({ show: false, message: '', variant: 'info' });
  
  const { upload, isLoading: isUploading } = useUploadFile();

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const res = await upload(file);
      if (res?.url) {
        setUploadedFileUrl(res.url);
        setUploadedFileType(file.type);
        setUploadAlert({ show: true, message: 'File uploaded successfully!', variant: 'success' });
      }
    } catch (err) {
      setUploadAlert({ show: true, message: 'File upload failed.', variant: 'danger' });
    } finally {
      setTimeout(() => {
        setUploadAlert({ show: false, message: '', variant: 'info' });
      }, 3000);
    }
  };

  const handleCopyUrl = () => {
    if (uploadedFileUrl) {
      navigator.clipboard.writeText(uploadedFileUrl);
      setUploadAlert({ show: true, message: 'URL copied to clipboard!', variant: 'info' });
      setTimeout(() => {
        setUploadAlert({ show: false, message: '', variant: 'info' });
      }, 2000);
    }
  };

  const isImage = uploadedFileType?.startsWith('image/');
  const isVideo = uploadedFileType?.startsWith('video/');
  const isPdf = uploadedFileType === 'application/pdf';

  return (
    <Card className="mb-4">
      <Card.Header>
        <h5 className="card-title mb-0">File Upload</h5>
      </Card.Header>
      <Card.Body>
        {uploadAlert.show && (
          <Alert 
            variant={uploadAlert.variant} 
            dismissible 
            onClose={() => setUploadAlert({ show: false, message: '', variant: 'info' })}
          >
            {uploadAlert.message}
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Upload File</Form.Label>
          <Form.Control
            type="file"
            onChange={handleFileUpload}
            disabled={isUploading}
          />
          {isUploading && (
            <div className="mt-2">
              <Spinner animation="border" size="sm" className="me-2" />
              Uploading...
            </div>
          )}
        </Form.Group>

        {uploadedFileUrl && (
          <div className="mt-3">
            <Form.Group className="mb-3">
              <Form.Label>Uploaded File URL</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  value={uploadedFileUrl}
                  readOnly
                />
                <Button variant="outline-primary btn-sm" onClick={handleCopyUrl}>
                  <i className="fa fa-copy"></i> Copy
                </Button>
              </div>
            </Form.Group>

            <div className="mt-3">
              <h6>Preview:</h6>
              {isImage && (
                <Image
                  src={uploadedFileUrl}
                  alt="Uploaded file"
                  style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain' }}
                  thumbnail
                />
              )}
              {isVideo && (
                <video
                  src={uploadedFileUrl}
                  controls
                  style={{ maxWidth: '100%', maxHeight: '400px' }}
                >
                  Your browser does not support the video tag.
                </video>
              )}
              {isPdf && (
                <div className="alert alert-info">
                  <i className="fa fa-file-pdf me-2"></i>
                  PDF file uploaded successfully.
                  <br />
                  <a 
                    href={uploadedFileUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="btn btn-sm btn-primary mt-2"
                  >
                    <i className="fa fa-external-link-alt me-1"></i> View PDF
                  </a>
                </div>
              )}
              {!isImage && !isVideo && !isPdf && (
                <div className="alert alert-info">
                  <i className="fa fa-file me-2"></i>
                  File uploaded. Preview not available for this file type.
                  <br />
                  <a 
                    href={uploadedFileUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="btn btn-sm btn-primary mt-2"
                  >
                    <i className="fa fa-external-link-alt me-1"></i> Open File
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default FileUploadSection;
